import { Component, ElementRef, forwardRef, input, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { I18nService } from '@app/core/i18n/i18n.service';
import { Country } from '@app/core/models/drupal8';
import { StaticValuesListService } from '@app/core/services/static-values-list/static-values-list.service';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'country-selector',
  templateUrl: './country-selector.component.html',
  styleUrls: ['./country-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelectorComponent),
      multi: true,
    },
  ],
})
export class CountrySelectorComponent implements OnDestroy {
  private static countries?: Country[] | any;

  @Input() needOnlyCountryCode = true;
  @Input() placeholder = '';
  @Input() iconPrepend?: string;
  showError = input(false);
  readonly = input(false);

  currentCenterModel = new FormControl(null);
  isDisabled = false;
  onChange: any;
  onTouch: any;
  @ViewChild('scroller', { static: true }) protected scroller?: ElementRef;
  protected countries$ = new BehaviorSubject<Country[]>([]);
  protected allCountries$ = new BehaviorSubject<Country[]>([]);
  protected currentCountry?: Country;
  protected filterFormControl = new FormControl();
  private needToSetValue?: string | Country;
  private unsubscribeAll = new Subject<void>();

  constructor(private i18nService: I18nService, private staticValuesListService: StaticValuesListService) {
    this.fetchCountries();
    this.i18nService.changedSelectedLanguage$.pipe(takeUntil(this.unsubscribeAll)).subscribe(() => {
      this.needToSetValue = this.currentCountry?.code;
      this.fetchCountries(true);
    });
    this.filterFormControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(() => {
      this.filterCountries();
    });
  }

  @ViewChild(NgbDropdown, { static: true }) set dropdown(dropdown: NgbDropdown) {
    if (!dropdown) {
      return;
    }
    dropdown?.openChange?.subscribe((isOpen: boolean) => {
      switch (isOpen) {
        case true:
          if (this.currentCountry && this.scroller) {
            setTimeout(() => {
              const element = document.getElementById('country-' + this.currentCountry?.code);
              if (element) {
                this.scroller?.nativeElement.scrollTo({
                  top: element.offsetTop - 60,
                  istant: true,
                });
              }
            }, 100);
          }
          break;
        case false:
          this.filterFormControl.reset();
          break;
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  getFlagClass(code: string): string {
    return code.toLocaleLowerCase();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
  writeValue(countryIn: string | Country): void {
    if (!countryIn) {
      delete this.currentCountry;
    }
    if (!CountrySelectorComponent.countries) {
      this.needToSetValue = countryIn;
      return;
    }

    this.setValue(countryIn);
  }

  protected selectCountry(country: Country) {
    this.currentCountry = country;
    this.onChange(this.needOnlyCountryCode ? this.currentCountry?.code : this.currentCountry);
    this.filterFormControl.reset();
  }

  protected clearCountry() {
    this.currentCountry = undefined;
    this.onChange(undefined);
  }

  private filterCountries(): void {
    const filterValue = this.filterFormControl.value;
    if (!filterValue) {
      this.countries$.next(this.allCountries$.value);
      return;
    }
    this.countries$.next(
      this.allCountries$.value.filter((country: Country) => {
        return country.name.toLowerCase().includes(filterValue.toLowerCase());
      })
    );
  }

  private setValue(countryIn: string | Country) {
    const index: any = CountrySelectorComponent.countries?.findIndex(
      (country: any) => country.code === (this.needOnlyCountryCode ? countryIn : (countryIn as Country).code)
    );
    this.currentCountry = index === -1 ? undefined : (CountrySelectorComponent.countries as any)[index];
  }
  private fetchCountries(force = false) {
    if (CountrySelectorComponent.countries && !force) {
      return;
    }
    delete CountrySelectorComponent.countries;
    return this.staticValuesListService.getCountries().subscribe({
      next: (data: Country[]) => {
        CountrySelectorComponent.countries = data;
        this.allCountries$.next(data);
        this.filterCountries();
        if (this.needToSetValue) {
          this.setValue(this.needToSetValue);
          delete this.needToSetValue;
        }
        return;
      },
    });
  }
}
